import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DogService } from '../services/dogService';
import { normalizeDogOwners, transformDogFormData, mapOwnerIdsToObjects } from '../utils/dataUtils';

/**
 * Custom hook for managing dog operations
 * Encapsulates dog-related state and business logic
 */
export function useDogs() {
    const { authorizedFetch } = useAuth();
    
    // State
    const [dogs, setDogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDogs, setTotalDogs] = useState(0);

    /**
     * Load dogs from API with pagination
     */
    const loadDogs = useCallback(async (params = {}) => {
        if (!authorizedFetch) return;
        
        setLoading(true);
        setError('');
        
        try {
            const finalParams = { page: 1, limit: 20, ...params };
            const response = await DogService.getAllDogs(authorizedFetch, finalParams);
            if (response && typeof response === 'object' && !Array.isArray(response)) {
                const normalizedDogs = normalizeDogOwners(response.data || []);
                setDogs(normalizedDogs);
                setPage(response.page || 1);
                setTotalPages(response.totalPages || 1);
                setTotalDogs(response.total ?? normalizedDogs.length);
            } else {
                const normalizedDogs = normalizeDogOwners(response || []);
                setDogs(normalizedDogs);
                setPage(1);
                setTotalPages(1);
                setTotalDogs(normalizedDogs.length);
            }
        } catch (err) {
            console.error('Failed to load dogs:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch]);

    /**
     * Create a new dog
     * @param {FormData|Object} formData - Dog form data (FormData for image uploads, Object for regular data)
     * @param {Array} allOwners - All available owners for mapping
     */
    const createDog = useCallback(async (formData, allOwners) => {
        setError('');
        setLoading(true);

        try {
            // If FormData, pass directly to service; if already has 'owners' array, pass as-is; otherwise transform
            let dogData;
            if (formData instanceof FormData) {
                dogData = formData;
            } else if (formData.owners && Array.isArray(formData.owners)) {
                // Data is already transformed (has owners array)
                dogData = formData;
            } else {
                // Data needs transformation (has primaryOwnerId/secondaryOwnerId)
                dogData = transformDogFormData(formData);
            }

            const createdDog = await DogService.createDog(dogData, authorizedFetch);

            // Map owner IDs to owner objects for UI display
            const ownerObjects = Array.isArray(createdDog.owners) && typeof createdDog.owners[0] === 'object'
                ? createdDog.owners
                : mapOwnerIdsToObjects(createdDog.owners, allOwners);

            const dogWithOwners = { ...createdDog, owners: ownerObjects };
            setDogs(prev => [dogWithOwners, ...prev]);

            return dogWithOwners;
        } catch (err) {
            console.error('Failed to create dog:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch]);

    /**
     * Update an existing dog
     * @param {string} dogId - ID of dog to update
     * @param {FormData|Object} formData - Updated dog form data (FormData for image uploads, Object for regular data)
     * @param {Array} allOwners - All available owners for mapping
     */
    const updateDog = useCallback(async (dogId, formData, allOwners) => {
        setError('');
        
        try {
            // If FormData, pass directly to service; if already has 'owners' array, pass as-is; otherwise transform
            let dogData;
            if (formData instanceof FormData) {
                dogData = formData;
            } else if (formData.owners && Array.isArray(formData.owners)) {
                // Data is already transformed (has owners array)
                dogData = formData;
            } else {
                // Data needs transformation (has primaryOwnerId/secondaryOwnerId)
                dogData = transformDogFormData(formData);
            }
            
            const updatedDog = await DogService.updateDog(dogId, dogData, authorizedFetch);
            
            // Map owner IDs to owner objects for UI display
            const ownerObjects = Array.isArray(updatedDog.owners) && typeof updatedDog.owners[0] === 'object'
                ? updatedDog.owners
                : mapOwnerIdsToObjects(updatedDog.owners, allOwners);
            
            const dogWithOwners = { ...updatedDog, owners: ownerObjects };
            setDogs(prev => prev.map(dog => dog._id === dogId ? dogWithOwners : dog));
            
            return dogWithOwners;
        } catch (err) {
            console.error('Failed to update dog:', err);
            setError(err.message);
            throw err;
        }
    }, [authorizedFetch]);

    /**
     * Delete a dog
     * @param {string} dogId - ID of dog to delete
     */
    const deleteDog = useCallback(async (dogId) => {
        setError('');
        
        try {
            await DogService.deleteDog(dogId, authorizedFetch);
            setDogs(prev => prev.filter(dog => dog._id !== dogId));
        } catch (err) {
            console.error('Failed to delete dog:', err);
            setError(err.message);
            throw err;
        }
    }, [authorizedFetch]);

    /**
     * Patch a single dog in the local list without a full reload.
     * Used when the modal saves partial updates (e.g. carta signature).
     */
    const patchDog = useCallback((updatedDog) => {
        setDogs(prev => prev.map(d => d._id === updatedDog._id ? { ...d, ...updatedDog } : d));
    }, []);

    /**
     * Clear any existing errors
     */
    const clearError = useCallback(() => setError(''), []);
    
    /**
     * Initialize data loading when authorizedFetch is available
     */
    useEffect(() => {
        if (authorizedFetch) {
            loadDogs();
        }
    }, [authorizedFetch, loadDogs]);

    return {
        dogs,
        loading,
        error,
        page,
        setPage,
        totalPages,
        totalDogs,
        loadDogs,
        createDog,
        updateDog,
        deleteDog,
        patchDog,
        clearError
    };
}
